"use client";

import { useEffect, useState } from "react";
import { addDays, format, isBefore, startOfDay } from "date-fns";
import type { Location, Vehicle } from "@/lib/types";
import { availableTimeOptions } from "@/lib/time-options";
import { BOOKING_RULES } from "@/lib/validation";
import DatePopover from "./DatePopover";

export interface SearchSelection {
  date_from: string | null;
  date_to: string | null;
  pickup_time: string;
  return_time: string;
  pickup_location_id: number | null;
  return_location_id: number | null;
}

export interface SpecFilters {
  transmission: string | null;
  body_type: string | null;
}

interface SearchFilterBlockProps {
  locations: Location[];
  vehicles: Vehicle[];
  initial: SearchSelection;
  specFilters: SpecFilters;
  onSearch: (selection: SearchSelection) => void;
  onSpecChange: (filters: SpecFilters) => void;
}

const TRANSMISSION_LABELS: Record<string, string> = {
  automatic: "Automatinė",
  manual: "Mechaninė",
};

const BODY_LABELS: Record<string, string> = {
  sedan: "Sedanas",
  hatchback: "Hečbekas",
  suv: "Visureigis",
  wagon: "Universalas",
  coupe: "Kupė",
  convertible: "Kabrioletas",
  van: "Mikroautobusas",
  pickup: "Pikapas",
  minivan: "Vienatūris",
};

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values)).sort();
}

export default function SearchFilterBlock({
  locations,
  vehicles,
  initial,
  specFilters,
  onSearch,
  onSpecChange,
}: SearchFilterBlockProps) {
  const now = new Date();
  const today = startOfDay(now);
  const maxDate = addDays(now, BOOKING_RULES.ADVANCE_DAYS);
  // If every slot for today has already passed (late evening) today is no longer
  // bookable — push the calendar floor to tomorrow so the picker can't land on a
  // day with an empty time list.
  const earliestPickup =
    availableTimeOptions(today, now).length > 0 ? today : addDays(today, 1);

  const [pickupDate, setPickupDate] = useState<Date | undefined>(
    initial.date_from ? new Date(initial.date_from) : undefined,
  );
  const [returnDate, setReturnDate] = useState<Date | undefined>(
    initial.date_to ? new Date(initial.date_to) : undefined,
  );
  const [pickupTime, setPickupTime] = useState(initial.pickup_time);
  const [returnTime, setReturnTime] = useState(initial.return_time);
  const [pickupLocationId, setPickupLocationId] = useState<number | null>(
    initial.pickup_location_id ?? locations[0]?.id ?? null,
  );
  const [returnLocationId, setReturnLocationId] = useState<number | null>(
    initial.return_location_id ?? initial.pickup_location_id ?? locations[0]?.id ?? null,
  );

  const pickupTimeOptions = availableTimeOptions(pickupDate, now);
  const returnTimeOptions = availableTimeOptions(returnDate, now);

  // When the selected day is today, drop time slots that have already passed. If
  // that leaves the current selection invalid, snap it to the earliest remaining
  // slot (and re-emit so the search reflects a bookable time).
  useEffect(() => {
    const opts = availableTimeOptions(pickupDate);
    if (opts.length > 0 && !opts.includes(pickupTime)) {
      setPickupTime(opts[0]);
      emit({ pickupTime: opts[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupDate]);
  useEffect(() => {
    const opts = availableTimeOptions(returnDate);
    if (opts.length > 0 && !opts.includes(returnTime)) {
      setReturnTime(opts[0]);
      emit({ returnTime: opts[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnDate]);

  function emit(overrides: Partial<{
    pickupDate: Date | undefined;
    returnDate: Date | undefined;
    pickupTime: string;
    returnTime: string;
    pickupLocationId: number | null;
    returnLocationId: number | null;
  }>) {
    const pd = "pickupDate" in overrides ? overrides.pickupDate : pickupDate;
    const rd = "returnDate" in overrides ? overrides.returnDate : returnDate;
    const pt = overrides.pickupTime ?? pickupTime;
    const rt = overrides.returnTime ?? returnTime;
    const pl = "pickupLocationId" in overrides ? overrides.pickupLocationId! : pickupLocationId;
    const rl = "returnLocationId" in overrides ? overrides.returnLocationId! : returnLocationId;
    onSearch({
      date_from: pd ? format(pd, "yyyy-MM-dd") : null,
      date_to: rd ? format(rd, "yyyy-MM-dd") : null,
      pickup_time: pt,
      return_time: rt,
      pickup_location_id: pl,
      return_location_id: rl,
    });
  }

  function handlePickupSelect(date: Date | undefined) {
    let nextReturn = returnDate;
    if (date && returnDate && isBefore(startOfDay(returnDate), startOfDay(date))) {
      nextReturn = undefined;
      setReturnDate(undefined);
    }
    setPickupDate(date);
    emit({ pickupDate: date, returnDate: nextReturn });
  }

  function handleReturnSelect(date: Date | undefined) {
    setReturnDate(date);
    emit({ returnDate: date });
  }

  const transmissions = uniqueSorted(vehicles.map((v) => v.transmission).filter(Boolean));
  const bodyTypes = uniqueSorted(vehicles.map((v) => v.body_type).filter(Boolean));

  const inputCls =
    "w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 sm:p-6 flex flex-col gap-4">
      {/* Row 1: dates + times */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Paėmimo data</label>
          <div className="flex items-center gap-2">
            <DatePopover
              value={pickupDate}
              onSelect={handlePickupSelect}
              disabled={{ before: earliestPickup, after: maxDate }}
            />
            <select
              value={pickupTimeOptions.includes(pickupTime) ? pickupTime : pickupTimeOptions[0] ?? pickupTime}
              onChange={(e) => {
                const v = e.target.value;
                setPickupTime(v);
                emit({ pickupTime: v });
              }}
              className="px-2 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {pickupTimeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Grąžinimo data</label>
          <div className="flex items-center gap-2">
            <DatePopover
              value={returnDate}
              onSelect={handleReturnSelect}
              disabled={{ before: pickupDate ?? earliestPickup, after: maxDate }}
              defaultMonth={pickupDate}
            />
            <select
              value={returnTimeOptions.includes(returnTime) ? returnTime : returnTimeOptions[0] ?? returnTime}
              onChange={(e) => {
                const v = e.target.value;
                setReturnTime(v);
                emit({ returnTime: v });
              }}
              className="px-2 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {returnTimeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Row 2: locations */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Paėmimo vieta</label>
          <select
            value={pickupLocationId ?? ""}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : null;
              setPickupLocationId(v);
              emit({ pickupLocationId: v });
            }}
            className={inputCls}
          >
            {locations.length === 0 && <option value="">—</option>}
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Grąžinimo vieta</label>
          <select
            value={returnLocationId ?? ""}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : null;
              setReturnLocationId(v);
              emit({ returnLocationId: v });
            }}
            className={inputCls}
          >
            {locations.length === 0 && <option value="">—</option>}
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 3: spec filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Pavarų dėžė</label>
          <select
            value={specFilters.transmission ?? ""}
            onChange={(e) =>
              onSpecChange({ ...specFilters, transmission: e.target.value || null })
            }
            className={inputCls}
          >
            <option value="">Visos</option>
            {transmissions.map((t) => (
              <option key={t} value={t}>{TRANSMISSION_LABELS[t] ?? t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Kėbulo tipas</label>
          <select
            value={specFilters.body_type ?? ""}
            onChange={(e) =>
              onSpecChange({ ...specFilters, body_type: e.target.value || null })
            }
            className={inputCls}
          >
            <option value="">Visi</option>
            {bodyTypes.map((b) => (
              <option key={b} value={b}>{BODY_LABELS[b] ?? b}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
